import * as React from "react";
import type { Session, User } from "@supabase/supabase-js";
import { useQueryClient } from "@tanstack/react-query";

import { supabase } from "@/integrations/supabase/client";

export type AppRole = "member" | "moderator" | "administrator" | "owner";
export type MemberType = "reader" | "writer";

export interface AuthProfile {
  id: string;
  username: string;
  display_name: string;
  avatar_url: string | null;
  bio: string | null;
  member_type: MemberType;
}

interface AuthContextValue {
  user: User | null;
  session: Session | null;
  profile: AuthProfile | null;
  roles: AppRole[];
  loading: boolean;
  /** Reader vs Writer experience. Never grants staff permissions. */
  memberType: MemberType;
  isWriter: boolean;
  isStaff: boolean;
  isAdmin: boolean;
  isOwner: boolean;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
}


const AuthContext = React.createContext<AuthContextValue | undefined>(undefined);

/**
 * How many AuthProviders are currently mounted in this browser tab.
 * Zero means any component calling useAuth is rendering outside the provider,
 * which is the single most common cause of a blank screen on boot.
 */
let mountedProviders = 0;

export interface AuthProviderStatus {
  /** True when at least one AuthProvider is mounted in the tree. */
  mounted: boolean;
  /** More than one is a bug: two providers means two competing sessions. */
  count: number;
}

export function getAuthProviderStatus(): AuthProviderStatus {
  return { mounted: mountedProviders > 0, count: mountedProviders };
}

/**
 * Auth access that never throws. Returns null outside the provider, so
 * diagnostics and error screens can report the problem instead of adding to it.
 */
export function useOptionalAuth(): AuthContextValue | null {
  return React.useContext(AuthContext) ?? null;
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  React.useEffect(() => {
    mountedProviders += 1;
    if (mountedProviders > 1) {
      console.warn(
        `[auth] ${mountedProviders} AuthProviders are mounted at once. ` +
          "Exactly one belongs in src/routes/__root.tsx; nested providers create " +
          "competing sessions and stale profile data.",
      );
    }
    return () => {
      mountedProviders -= 1;
    };
  }, []);

  const [session, setSession] = React.useState<Session | null>(null);
  const [profile, setProfile] = React.useState<AuthProfile | null>(null);
  const [roles, setRoles] = React.useState<AppRole[]>([]);
  const [loading, setLoading] = React.useState(true);
  const queryClient = useQueryClient();

  const loadAccount = React.useCallback(async (userId: string) => {
    const [{ data: profileRow }, { data: roleRows }] = await Promise.all([
      supabase
        .from("profiles")
        .select("id, username, display_name, avatar_url, bio, member_type")
        .eq("id", userId)
        .maybeSingle(),
      supabase.from("user_roles").select("role").eq("user_id", userId),
    ]);
    setProfile((profileRow as AuthProfile | null) ?? null);
    setRoles(((roleRows ?? []) as { role: AppRole }[]).map((r) => r.role));
  }, []);

  React.useEffect(() => {
    let active = true;

    const { data: subscription } = supabase.auth.onAuthStateChange((event, nextSession) => {
      if (!active) return;
      setSession(nextSession);
      if (nextSession?.user) {
        // Defer Supabase calls out of the auth callback.
        setTimeout(() => {
          void loadAccount(nextSession.user.id);
        }, 0);
      } else {
        setProfile(null);
        setRoles([]);
      }
      if (event === "SIGNED_OUT") {
        queryClient.clear();
      }
    });

    void supabase.auth.getSession().then(async ({ data }) => {
      if (!active) return;
      setSession(data.session);
      if (data.session?.user) await loadAccount(data.session.user.id);
      setLoading(false);
    });

    return () => {
      active = false;
      subscription.subscription.unsubscribe();
    };
  }, [loadAccount, queryClient]);

  const refreshProfile = React.useCallback(async () => {
    if (session?.user) await loadAccount(session.user.id);
  }, [session, loadAccount]);

  const signOut = React.useCallback(async () => {
    await queryClient.cancelQueries();
    queryClient.clear();
    await supabase.auth.signOut();
  }, [queryClient]);

  const value = React.useMemo<AuthContextValue>(
    () => ({
      user: session?.user ?? null,
      session,
      profile,
      roles,
      loading,
      memberType: profile?.member_type ?? "reader",
      isWriter: profile?.member_type === "writer",
      isStaff:
        roles.includes("moderator") || roles.includes("administrator") || roles.includes("owner"),
      isAdmin: roles.includes("administrator") || roles.includes("owner"),
      isOwner: roles.includes("owner"),
      signOut,
      refreshProfile,
    }),

    [session, profile, roles, loading, signOut, refreshProfile],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/** Everything known about a failed useAuth() call, in one flat object. */
export interface AuthFailureBreadcrumb {
  event: "auth.provider_missing";
  route: string;
  /** How many AuthProviders were mounted at the moment of failure. */
  providerMountCount: number;
  /** Whether the context could be read from this point in the tree. */
  reachableFromTree: false;
  /** The file to edit to fix it. */
  suggestedFixPath: string;
  suggestedFix: string;
  timestamp: string;
}

/** Guesses the layout file that should own the provider for a given route. */
export function suggestedProviderFile(route: string, mountCount: number): string {
  if (mountCount === 0) return "src/routes/__root.tsx";
  // A provider exists but isn't above this component — the nearest layout for
  // the current URL is the most likely place the subtree escaped from.
  const first = route.split("/").filter(Boolean)[0];
  if (!first) return "src/routes/__root.tsx";
  return `src/routes/${first}/route.tsx (or src/routes/__root.tsx)`;
}

/** Builds the breadcrumb for a missing provider; exported so tests can assert on it. */
export function buildAuthFailureBreadcrumb(route: string): AuthFailureBreadcrumb {
  const suggestedFixPath = suggestedProviderFile(route, mountedProviders);
  return {
    event: "auth.provider_missing",
    route,
    providerMountCount: mountedProviders,
    reachableFromTree: false,
    suggestedFixPath,
    suggestedFix:
      mountedProviders === 0
        ? `No AuthProvider is mounted anywhere. Wrap the app's layout in <AuthProvider> in ${suggestedFixPath}, around <Outlet />.`
        : `An AuthProvider is mounted (${mountedProviders}), but this component renders outside it — it is likely in a portal, a second React root, or above the provider in ${suggestedFixPath}. Move it inside the provider, or switch to useOptionalAuth().`,
    timestamp: new Date().toISOString(),
  };
}

export function useAuth() {
  const ctx = React.useContext(AuthContext);
  if (!ctx) {
    // Log before throwing: the thrown error alone doesn't say which screen
    // broke or how to fix it, and the overlay often hides the component stack.
    const route = typeof window === "undefined" ? "(server render)" : window.location.pathname;
    const breadcrumb = buildAuthFailureBreadcrumb(route);
    console.warn(
      [
        `[auth] useAuth() was called on "${route}" without an AuthProvider above it.`,
        breadcrumb.suggestedFix,
        "Components that must render either way can use useOptionalAuth() instead.",
      ].join("\n"),
    );
    // Structured twin of the message above, for log search and correlation.
    console.warn("[auth] breadcrumb", breadcrumb);
    throw new Error("useAuth must be used inside AuthProvider");
  }
  return ctx;
}

